import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { apiServices } from "../../../../core/utilities/ApiServices";

type ReadStatus = "read" | "no_confirmation" | "unavailable" | "checking" | "error";

export const InstantMessageReceipt = ({ viewerId, otherId, message, sentByMe }: {
  viewerId: string; otherId: string; message: string; sentByMe: boolean;
}) => {
  const field = useRef<HTMLTextAreaElement>(null);
  const authorization = String(apiServices.defaults.headers.common.Authorization || "");
  const identity = JSON.stringify([viewerId, otherId, message, sentByMe, authorization]);
  const [saved, setSaved] = useState<{ identity: string; status: ReadStatus; returned?: boolean }>();
  const [retry, setRetry] = useState(0);
  const status = saved?.identity === identity ? saved.status : "checking";

  useEffect(() => {
    if (!viewerId || !otherId || !message || !authorization) return;
    let active = true;
    let visible = false;
    let confirmed = false;
    let sending = false;
    let generation = 0;
    const controller = new AbortController();
    const headers = { Authorization: authorization, "x-client-platform": "web" };
    // A late receipt response must never run the shared session-expiry interceptor
    // or replace another account's status after a sign-in change.
    const refresh = async () => {
      if (document.visibilityState !== "visible" || !active) return;
      if (sentByMe) {
        const request = ++generation;
        try {
          const response = await axios.get(`${apiServices.defaults.baseURL}/match/instant-message-status`, {
            params: { user_id: otherId }, headers, signal: controller.signal,
          });
          const next = response.data?.status;
          if (!active || request !== generation) return;
          setSaved({ identity, status: ["read", "no_confirmation", "unavailable"].includes(next) ? next : "error", returned: response.data?.credit_returned === true });
        } catch {
          if (active && request === generation) setSaved({ identity, status: "error" });
        }
      } else if (visible && !confirmed && !sending) {
        sending = true;
        try {
          await axios.post(`${apiServices.defaults.baseURL}/match/instant-message-read`, {
            user_id: otherId, message,
          }, { headers, signal: controller.signal });
          confirmed = true;
        } catch {
          // No false confirmation on connection failure; a later visible entry can retry.
        } finally { sending = false; }
      }
    };
    const observer = !sentByMe && typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio >= 1;
        void refresh();
      }, { threshold: 1 }) : undefined;
    if (field.current) observer?.observe(field.current);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    void refresh();
    return () => {
      active = false;
      controller.abort();
      observer?.disconnect();
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [viewerId, otherId, message, sentByMe, authorization, identity, retry]);

  return <>
    <textarea ref={field} aria-label={sentByMe ? "Your Instant Message" : "Received Instant Message"}
      className="w-full text-sm resize-none rounded-lg border border-grayLight px-4 py-4 placeholder:text-grayLight2 placeholder:select-none outline-none"
      maxLength={160} value={message} readOnly />
    {sentByMe && <p className="text-sm text-graySecondary" role="status">
      {status === "read" ? "Read" : status === "no_confirmation" ? "Sent · No read confirmation yet"
        : status === "unavailable" ? "Read status is unavailable for older introductions."
          : status === "checking" ? "Checking read status…" : <>Read status unavailable. <button type="button" className="text-primary underline" onClick={() => setRetry(value => value + 1)}>Retry</button></>}
    </p>}
    {sentByMe && status === "no_confirmation" && !saved?.returned && <p className="text-sm text-graySecondary">If there is no read confirmation after seven full days, one Instant Message credit is returned automatically.</p>}
    {sentByMe && saved?.identity === identity && saved.returned && <p className="text-sm text-graySecondary">1 Instant Message credit returned.</p>}
  </>;
};
